import { useCallback, useState, useEffect } from 'react';
import jwtDecode from 'jwt-decode';
import request from 'utils/request_ajax';
import supportsLocalStorage from 'utils/supportsLocalStorage';

/**
 * Refreshes skills token if expired
 * Grants new skills token if none are saved
 *
 * @example
 * const { token, error, status } = useSkillsToken();
 *
 */

export const SKILLS_TOKEN_STATES = {
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error',
};

const useSkillsToken = () => {
  const [status, setStatus] = useState(SKILLS_TOKEN_STATES.LOADING);
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);

  const getAndSetToken = useCallback(async () => {
    try {
      request
        .get(
          `${window.location.protocol}//${window.location.host}/sessions/token`,
          null,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          },
        )
        .then(loginResponse => {
          localStorage.setItem('skills-token', loginResponse.token);
          setToken(loginResponse.token);
          setStatus(SKILLS_TOKEN_STATES.SUCCESS);
        })
        .catch(err => {
          setStatus(SKILLS_TOKEN_STATES.ERROR);
          setError(err);
          throw Error(err);
        });
    } catch (err) {
      setStatus(SKILLS_TOKEN_STATES.ERROR);
      setError(err);
      throw Error(err);
    }
  }, [setToken]);

  useEffect(() => {
    const tokenObject = localStorage.getItem('skills-token');
    let parsedToken = null;
    try {
      parsedToken = tokenObject ? jwtDecode(tokenObject) : null;
    } catch (err) {
      console.warn(err);
      setError(err);
    }
    const now = new Date();
    const newDate = new Date(parsedToken?.exp * 1000);
    const shouldGetNewToken = now > newDate || !parsedToken;
    if (shouldGetNewToken) {
      getAndSetToken();
    } else {
      setToken(tokenObject);
      setStatus(SKILLS_TOKEN_STATES.SUCCESS);
    }
  }, [getAndSetToken]);

  if (!supportsLocalStorage) return {};
  return { token, error, status };
};

export default useSkillsToken;
